// ============================================
// UPDATED: /frontend/src/pages/UserDashboard.jsx
// With DashboardLayout wrapper and enhanced logging
// ============================================

import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../api/dashboard.api';
import { Loader2, Share2, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/layout/DashboardLayout';


// Components
import ProfileOverview from '../components/dashboard/ProfileOverview';
import SubscriptionStatus from '../components/dashboard/SubscriptionStatus';
import AdPerformance from '../components/dashboard/AdPerformance';
import MyListingsManager from '../components/dashboard/MyListingsManager';
import QuickActions from '../components/dashboard/QuickActions';

export default function UserDashboard() {
  console.log('========================================');
  console.log('[UserDashboard] 🎨 Component mounted');
  console.log('========================================');

  const [dashboard, setDashboard] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

    useEffect(() => {
      console.log('[UserDashboard] 🔄 useEffect triggered - fetching data');
      fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
    console.log('[UserDashboard] 📡 Starting data fetch...');
    console.log('[UserDashboard] ⏰ Timestamp:', new Date().toISOString());
    
    setLoading(true);

    try {
      console.log('[UserDashboard] 🔵 Calling dashboardAPI.getOverview()');
      const overviewRes = await dashboardAPI.getOverview();

      console.log('[UserDashboard] ✅ Overview received:');
      console.log('  - User:', overviewRes.dashboard?.user?.email);
      console.log('  - Subscription:', overviewRes.dashboard?.subscription?.tier);
      console.log('  - Stats:', overviewRes.dashboard?.stats);
      
      // Small delay before next request
      await new Promise(resolve => setTimeout(resolve, 300));

      console.log('[UserDashboard] 🔵 Calling dashboardAPI.getListings()');
      const listingsRes = await dashboardAPI.getListings();

      console.log('[UserDashboard] ✅ Listings received:');
      console.log('  - Count:', listingsRes.listings?.length);
      console.log('  - Listings:', listingsRes.listings);

      setDashboard(overviewRes.dashboard);
      setListings(listingsRes.listings);
      setError(null);
      
      console.log('[UserDashboard] 💾 State updated successfully');
    } catch (err) {
      console.error('[UserDashboard] ❌ Fetch error:');
      console.error('  - Error:', err);
      console.error('  - Response:', err.response);
      console.error('  - Status:', err.response?.status);
      console.error('  - Data:', err.response?.data);
      
      setError(err.response?.data?.message || 'Failed to load dashboard');
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      console.log('[UserDashboard] 🏁 Fetch complete');
    }
  };

  // Loading State
  if (loading) {
    console.log('[UserDashboard] ⏳ Rendering loading state');
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading your dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error State
  if (error || !dashboard) {
    console.log('[UserDashboard] 🔴 Rendering error state:', error);
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-destructive mb-4">{error || 'Failed to load dashboard'}</p>
            <button 
              onClick={() => {
                console.log('[UserDashboard] 🔄 Retry button clicked');
                fetchDashboard();
              }}
              className="text-primary underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  console.log('[UserDashboard] 🎨 Rendering dashboard with data');

  return (
    <DashboardLayout>
     

      {/* Top Row: Profile & Subscription */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <ProfileOverview user={dashboard.user} onUpdate={fetchDashboard} />
        <SubscriptionStatus subscription={dashboard.subscription} />
      </div>

      {/* Second Row: Performance & Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-2">
          <AdPerformance stats={dashboard.stats} />
        </div>
        <QuickActions />
      </div>

      {/* Listings Manager */}
      <MyListingsManager listings={listings} onUpdate={fetchDashboard} />
    </DashboardLayout>
  );
}

